import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import apiService from '../services/api';
import { Account } from '../types';
import {
  AccountTierContextResponse,
  TierContextSummaryResponse,
} from '../types/tierContext';
import { RiskTierNumber, TIER_CATALOG } from '../tiers/tierCatalog';
import { TierFilter } from '../components/portfolio/TierSpectrumRail';

function normalizeTierContext(
  ctx: AccountTierContextResponse,
): AccountTierContextResponse {
  return {
    ...ctx,
    tiers: ctx.tiers.map((row) => ({
      ...row,
      catalog: TIER_CATALOG[row.riskTier],
    })),
  };
}

const STORAGE_ACCOUNT = 'marlin.selectedAccountId';
const STORAGE_TIER_FILTER = 'marlin.tierFilter';

interface TierPortfolioContextValue {
  accounts: Account[];
  accountsLoading: boolean;
  selectedAccountId: string | null;
  setSelectedAccountId: (id: string | null) => void;
  tierFilter: TierFilter;
  setTierFilter: (f: TierFilter) => void;
  accountContext: AccountTierContextResponse | null;
  summary: TierContextSummaryResponse | null;
  contextLoading: boolean;
  refreshContext: () => Promise<void>;
}

const TierPortfolioContext = createContext<TierPortfolioContextValue | null>(
  null,
);

export const TierPortfolioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_ACCOUNT),
  );
  const [tierFilter, setTierFilterState] = useState<TierFilter>(() => {
    const raw = localStorage.getItem(STORAGE_TIER_FILTER);
    if (raw === 'all') return 'all';
    const n = parseInt(raw ?? '', 10);
    if (n >= 1 && n <= 6) return n as RiskTierNumber;
    return 'all';
  });
  const [accountContext, setAccountContext] =
    useState<AccountTierContextResponse | null>(null);
  const [summary, setSummary] = useState<TierContextSummaryResponse | null>(
    null,
  );
  const [contextLoading, setContextLoading] = useState(false);

  const setSelectedAccountId = useCallback((id: string | null) => {
    setSelectedAccountIdState(id);
    if (id) localStorage.setItem(STORAGE_ACCOUNT, id);
    else localStorage.removeItem(STORAGE_ACCOUNT);
  }, []);

  const setTierFilter = useCallback((f: TierFilter) => {
    setTierFilterState(f);
    localStorage.setItem(STORAGE_TIER_FILTER, String(f));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setAccountsLoading(true);
      try {
        const list = await apiService.getAccounts();
        if (cancelled) return;
        setAccounts(list);
        if (list.length > 0 && !selectedAccountId) {
          setSelectedAccountId(list[0].id);
        } else if (
          selectedAccountId &&
          !list.some((a) => a.id === selectedAccountId)
        ) {
          setSelectedAccountId(list[0]?.id ?? null);
        }
      } catch {
        if (!cancelled) setAccounts([]);
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedAccountId, setSelectedAccountId]);

  const refreshContext = useCallback(async () => {
    setContextLoading(true);
    try {
      const summaryData = await apiService.getTierContextSummary();
      setSummary({
        ...summaryData,
        accounts: summaryData.accounts.map(normalizeTierContext),
      });
      const accountId =
        selectedAccountId ??
        summaryData.accounts[0]?.accountId ??
        null;
      if (accountId) {
        const raw =
          summaryData.accounts.find((a) => a.accountId === accountId) ??
          (await apiService.getAccountTierContext(accountId));
        setAccountContext(normalizeTierContext(raw));
      } else {
        setAccountContext(null);
      }
    } catch {
      setSummary(null);
      if (selectedAccountId) {
        try {
          const raw = await apiService.getAccountTierContext(selectedAccountId);
          setAccountContext(normalizeTierContext(raw));
        } catch {
          setAccountContext(null);
        }
      }
    } finally {
      setContextLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    refreshContext();
  }, [refreshContext]);

  const value = useMemo(
    () => ({
      accounts,
      accountsLoading,
      selectedAccountId,
      setSelectedAccountId,
      tierFilter,
      setTierFilter,
      accountContext,
      summary,
      contextLoading,
      refreshContext,
    }),
    [
      accounts,
      accountsLoading,
      selectedAccountId,
      setSelectedAccountId,
      tierFilter,
      setTierFilter,
      accountContext,
      summary,
      contextLoading,
      refreshContext,
    ],
  );

  return (
    <TierPortfolioContext.Provider value={value}>
      {children}
    </TierPortfolioContext.Provider>
  );
};

export function useTierPortfolio(): TierPortfolioContextValue {
  const ctx = useContext(TierPortfolioContext);
  if (!ctx) {
    throw new Error('useTierPortfolio must be used within TierPortfolioProvider');
  }
  return ctx;
}

/** Safe when provider is optional (e.g. public pages). */
export function useTierPortfolioOptional(): TierPortfolioContextValue | null {
  return useContext(TierPortfolioContext);
}
